// The 60fps game loop advances a single state ref in place (see SolarSurge.tsx);
// the engine stays deterministic and fully Jest-simulatable from a seed, which
// is what "pure engine" requires here.

import {
  BATTERY_R,
  CLOUD_W,
  CLOUD_W_MAX,
  GRAVITY,
  GROUND_H,
  MARGIN,
  MAX_CLOUD_H,
  MAX_VY,
  MILESTONES,
  MIN_CLOUD_H,
  MULT_MAX,
  MULT_TIME,
  STAR_COUNT,
  SUN_R,
  SUN_X,
  THRUST,
  TIME_SCALE,
} from './constants';
import { summarize } from './conversions';
import { effectiveScrollSpeed, LEVELS, levelForDistance } from './levels';
import { nextRandom } from './rng';
import { CloudBand, GameState, Input, RunSummary } from './types';

const makeStars = (state: GameState): void => {
  state.stars = [];
  for (let i = 0; i < STAR_COUNT; i += 1) {
    state.stars.push({
      r: nextRandom(state) < 0.3 ? 2 : 1,
      x: nextRandom(state) * state.w,
      y: nextRandom(state) * (state.h - GROUND_H),
    });
  }
};

/** Randomised horizontal distance to the next cloud group (unpredictable rhythm;
 *  the occasional tight cluster fuses into a thicker wall). */
const nextAdvance = (state: GameState): number => {
  const base = state.level.spacing;
  if (nextRandom(state) < 0.08) return base * (0.6 + nextRandom(state) * 0.2);
  return base * (0.9 + nextRandom(state) * 0.7);
};

/**
 * Build one cloud group around a meandering corridor (the helicopter-game feel).
 * The corridor centre drifts via a bounded random walk, and each group is
 * randomly a full top+bottom corridor, a single wall to fly over/under, or
 * free-floating mid clouds, for an unpredictable, naturally flowing course.
 */
const buildBands = (state: GameState): CloudBand[] => {
  const pTop = MARGIN;
  const pBot = state.h - GROUND_H - MARGIN;
  const playH = pBot - pTop;
  const { minGap } = state.level;
  const cw = () => Math.min(CLOUD_W_MAX, 48 + nextRandom(state) * (24 + state.level.index * 5));

  const drift = 24 + state.level.index * 4;
  state.corridorY += (nextRandom(state) - 0.5) * 2 * drift;

  const extra = Math.max(40, playH * (0.6 - state.level.index * 0.05));
  const gap = Math.min(playH - 36, minGap + nextRandom(state) * extra);
  const half = gap / 2;
  state.corridorY = Math.max(pTop + half, Math.min(pBot - half, state.corridorY));
  const cTop = state.corridorY - half;
  const cBot = state.corridorY + half;

  const bothP = 0.12 + state.level.index * 0.05;
  const singleP = 0.34;
  const roll = nextRandom(state);
  let topWall = false;
  let botWall = false;
  const softEntry = state.level.index === 3 && state.colsSinceLevel < 2;
  if (softEntry) {
    if (nextRandom(state) < 0.5) topWall = true;
    else botWall = true;
  } else if (roll < bothP) {
    topWall = true;
    botWall = true;
  } else if (roll < bothP + singleP) {
    if (nextRandom(state) < 0.5) topWall = true;
    else botWall = true;
  }

  const bands: CloudBand[] = [];
  if (topWall && cTop - pTop > 12) bands.push({ bot: cTop, seed: nextRandom(state), top: pTop, w: cw() });
  if (botWall && pBot - cBot > 12) bands.push({ bot: pBot, seed: nextRandom(state), top: cBot, w: cw() });

  if (!topWall && !botWall) {
    const th = Math.min(MAX_CLOUD_H, MIN_CLOUD_H + nextRandom(state) * (28 + state.level.index * 8));
    bands.push({ bot: state.corridorY + th / 2, seed: nextRandom(state), top: state.corridorY - th / 2, w: cw() });
    if (state.level.index >= 3 && nextRandom(state) < 0.45) {
      const off = (nextRandom(state) < 0.5 ? -1 : 1) * (minGap + 24 + nextRandom(state) * 46);
      const ny = Math.max(pTop + 8, Math.min(pBot - 8, state.corridorY + off));
      const th2 = MIN_CLOUD_H + nextRandom(state) * 22;
      bands.push({ bot: ny + th2 / 2, seed: nextRandom(state), top: ny - th2 / 2, w: cw() });
    }
  } else if (topWall && botWall && state.level.index >= 5 && nextRandom(state) < 0.15) {
    const nub = 14 + nextRandom(state) * 16;
    const lo = cTop + minGap + nub / 2;
    const hi = cBot - minGap - nub / 2;
    if (hi > lo) {
      const ny = lo + nextRandom(state) * (hi - lo);
      bands.push({ bot: ny + nub / 2, seed: nextRandom(state), top: ny - nub / 2, w: cw() * 0.8 });
    }
  }

  return bands;
};

/**
 * Tuck a battery into the tightest navigable gap of a column, a deliberate
 * detour that rewards precise flying (like a 1-up in a tricky nook).
 */
const maybeSpawnBattery = (state: GameState, x: number, bands: CloudBand[]): void => {
  const minCols = state.level.index <= 2 ? 3 : 4;
  const forced = state.sinceBattery >= 8;
  if (state.sinceBattery < minCols) return;
  if (!forced && nextRandom(state) > 0.22) return;

  const pTop = MARGIN;
  const pBot = state.h - GROUND_H - MARGIN;
  const spans: [number, number][] = [];
  let prev = pTop;
  bands.forEach((b) => {
    spans.push([prev, b.top]);
    prev = b.bot;
  });
  spans.push([prev, pBot]);

  const clearance = BATTERY_R * 2 + 10;
  const reachable = spans.filter(([a, b]) => b - a >= clearance);
  if (!reachable.length) return;
  reachable.sort((p, q) => p[1] - p[0] - (q[1] - q[0]));
  const [a, b] = reachable[0];

  state.batteries.push({
    bob: nextRandom(state) * Math.PI * 2,
    collected: false,
    value: 5 + state.level.index * 2,
    x: x + CLOUD_W / 2,
    y: (a + b) / 2,
  });
  state.sinceBattery = 0;
};

const spawnObstacle = (state: GameState, x: number): void => {
  const bands = buildBands(state);
  state.obstacles.push({ bands, passed: false, x });
  state.sinceBattery += 1;
  state.colsSinceLevel += 1;
  maybeSpawnBattery(state, x, bands);
};

/** Confetti burst rising from the panel strip on a score milestone. */
const spawnMilestoneBurst = (state: GameState): void => {
  for (let i = 0; i < 30; i += 1) {
    state.particles.push({
      color: nextRandom(state) < 0.5 ? '#FFD23F' : '#27E08A',
      life: 0.9 + nextRandom(state) * 0.6,
      vx: (nextRandom(state) - 0.5) * 70,
      vy: -(70 + nextRandom(state) * 150),
      x: nextRandom(state) * state.w,
      y: state.h - GROUND_H,
    });
  }
};

const checkCollision = (state: GameState): boolean => {
  if (state.sunY - SUN_R <= 0) return true; // ceiling
  if (state.sunY + SUN_R >= state.h - GROUND_H) return true; // panels
  const pad = 3;
  return state.obstacles.some((o) =>
    o.bands.some((band) => {
      const left = o.x + pad;
      const right = o.x + band.w - pad;
      if (SUN_X + SUN_R < left || SUN_X - SUN_R > right) return false;
      const cx = Math.max(left, Math.min(SUN_X, right));
      const cy = Math.max(band.top + pad, Math.min(state.sunY, band.bot - pad));
      const ddx = SUN_X - cx;
      const ddy = state.sunY - cy;
      return ddx * ddx + ddy * ddy < SUN_R * SUN_R;
    })
  );
};

const buildSummary = (state: GameState): RunSummary => {
  const isHighScore = state.energy > state.highScore;
  if (isHighScore) state.highScore = state.energy;
  return {
    ...summarize(state.energy),
    batteries: state.batteriesCollected,
    distanceM: state.distance,
    energyKwh: state.energy,
    isHighScore,
    maxMultiplier: state.maxMult,
    storageKwh: state.storageKwh,
    topLevel: state.level.index,
    topLevelTitle: state.level.title,
  };
};

/** Reset the world in place and hold in attract mode. */
export const resetWorld = (state: GameState): void => {
  state.sunY = state.h / 2;
  state.vy = 0;
  state.distance = 0;
  state.energy = 0;
  state.obstacles = [];
  state.batteries = [];
  state.pops = [];
  state.particles = [];
  state.milestoneHit = [];
  state.milestoneFlash = 0;
  state.milestoneText = '';
  state.colsSinceLevel = 0;
  state.batteriesCollected = 0;
  state.storageKwh = 0;
  state.sinceBattery = 0;
  state.comboMult = 1;
  state.multTimer = 0;
  state.maxMult = 1;
  state.corridorY = state.h / 2;
  state.thrusting = false;
  state.flash = 0;
  state.grace = 0;
  state.level = LEVELS[0];
  state.flashLevel = LEVELS[0];
  state.summary = null;
  state.events = [];
  // First-cloud delay: start further off-screen so the player gets ~1.5s of
  // unobstructed flight before the first obstacle arrives.
  let x = state.w + 180;
  for (let i = 0; i < 5; i += 1) {
    spawnObstacle(state, x);
    x += nextAdvance(state);
  }
};

type InitOptions = { h: number; highScore?: number; seed?: number; w: number };

export const createInitialState = ({ h, highScore = 0, seed = 1, w }: InitOptions): GameState => {
  const state: GameState = {
    batteries: [],
    batteriesCollected: 0,
    bob: 0,
    colsSinceLevel: 0,
    comboMult: 1,
    corridorY: h / 2,
    distance: 0,
    energy: 0,
    events: [],
    flash: 0,
    flashLevel: LEVELS[0],
    grace: 0,
    h,
    highScore,
    level: LEVELS[0],
    maxMult: 1,
    milestoneFlash: 0,
    milestoneHit: [],
    milestoneText: '',
    multTimer: 0,
    obstacles: [],
    particles: [],
    pops: [],
    rngState: Math.trunc(seed),
    sinceBattery: 0,
    stars: [],
    status: 'idle',
    storageKwh: 0,
    summary: null,
    sunY: h / 2,
    thrusting: false,
    vy: 0,
    w,
  };
  makeStars(state);
  resetWorld(state);
  return state;
};

/** Begin a run: hold the sun for a grace beat before gravity engages. */
export const startRun = (state: GameState): void => {
  resetWorld(state);
  state.status = 'running';
  state.grace = 1.2;
  state.level = LEVELS[0];
  state.flash = 0; // no opening banner, only the "GET READY!" cue
  state.flashLevel = LEVELS[0];
};

/** Arm a fresh run but wait in attract mode for the player's first input. */
export const armReady = (state: GameState): void => {
  resetWorld(state);
  state.status = 'ready';
};

/** Re-fit the logical size when the canvas box changes shape. */
export const setDims = (state: GameState, w: number, h: number): void => {
  if (w === state.w && h === state.h) return;
  state.w = w;
  state.h = h;
  // Only rebuild the world (and the star field, which draws from the RNG) before a run.
  // Rebuilding mid-run or on the game-over screen would advance rngState from an incidental
  // resize (breaking the seeded-replay guarantee) and wipe the live or finished world; in
  // those states just keep the sun in bounds.
  if (state.status === 'idle' || state.status === 'ready') {
    makeStars(state);
    resetWorld(state);
  } else {
    state.sunY = Math.max(SUN_R, Math.min(h - GROUND_H - SUN_R, state.sunY));
  }
};

const isAttract = (state: GameState): boolean => state.status === 'idle' || state.status === 'ready';

/** Advance the simulation one frame. Mutates the state ref (game-loop pattern). */
export const step = (state: GameState, input: Input, dt: number): void => {
  if (state.status === 'paused' || state.status === 'over') return;
  state.thrusting = input.thrust;

  const speed = state.status === 'running' ? effectiveScrollSpeed(state.level, state.distance) : 60;

  const dx = speed * dt;
  state.obstacles.forEach((o) => {
    o.x -= dx;
  });
  state.batteries.forEach((b) => {
    b.x -= dx;
  });
  if (state.obstacles.length && state.obstacles[0].x < -CLOUD_W_MAX) state.obstacles.shift();
  state.batteries = state.batteries.filter((b) => b.x > -BATTERY_R * 2 && !b.collected);
  const lastObstacle = state.obstacles[state.obstacles.length - 1];
  if (lastObstacle && lastObstacle.x < state.w) spawnObstacle(state, lastObstacle.x + nextAdvance(state));

  state.pops.forEach((p) => {
    p.y -= 22 * dt;
    p.life -= dt * 1.3;
  });
  state.pops = state.pops.filter((p) => p.life > 0);

  state.particles.forEach((p) => {
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vy += 220 * dt;
    p.life -= dt;
  });
  if (state.particles.length) state.particles = state.particles.filter((p) => p.life > 0);

  if (isAttract(state)) {
    state.bob += dt;
    state.sunY = state.h / 2 + Math.sin(state.bob * 1.6) * 26;
    return;
  }

  // Grace window: hold the sun steady so the player can get on the controls.
  if (state.grace > 0) {
    state.grace = Math.max(0, state.grace - dt);
    state.bob += dt;
    state.vy = 0;
    state.sunY = state.h / 2 + Math.sin(state.bob * 1.6) * 6;
    return;
  }

  if (state.multTimer > 0) {
    state.multTimer = Math.max(0, state.multTimer - dt);
    if (state.multTimer === 0) state.comboMult = 1;
  }

  state.distance += (speed * dt) / 10; // ~metres
  state.energy += ((state.level.powerKw * dt * TIME_SCALE) / 3600) * state.comboMult;

  state.vy += (GRAVITY - (state.thrusting ? THRUST : 0)) * dt;
  state.vy = Math.max(-MAX_VY, Math.min(MAX_VY, state.vy));
  state.sunY += state.vy * dt;
  state.bob += dt;

  const lvl = levelForDistance(state.distance);
  if (lvl.index !== state.level.index) {
    state.level = lvl;
    state.flash = 1.8;
    state.flashLevel = lvl;
    state.colsSinceLevel = 0;
    state.events.push({ level: lvl, type: 'levelUp' });
  }
  if (state.flash > 0) state.flash = Math.max(0, state.flash - dt);

  MILESTONES.forEach((m) => {
    if (state.energy >= m && !state.milestoneHit.includes(m)) {
      state.milestoneHit.push(m);
      state.milestoneFlash = 1.1;
      state.milestoneText = `${m} kWh!`;
      spawnMilestoneBurst(state);
      state.events.push({ kwh: m, type: 'milestone' });
    }
  });
  if (state.milestoneFlash > 0) state.milestoneFlash = Math.max(0, state.milestoneFlash - dt);

  state.obstacles.forEach((o) => {
    if (!o.passed && o.x + CLOUD_W < SUN_X) {
      o.passed = true;
      state.energy += 0.5 * state.level.index * state.comboMult;
      state.events.push({ type: 'point' });
    }
  });

  state.batteries.forEach((b) => {
    if (b.collected) return;
    const by = b.y + Math.sin(state.bob * 3 + b.bob) * 4;
    const dxb = SUN_X - b.x;
    const dyb = state.sunY - by;
    if (dxb * dxb + dyb * dyb <= (SUN_R + BATTERY_R) * (SUN_R + BATTERY_R)) {
      b.collected = true;
      state.energy += b.value;
      state.storageKwh += b.value;
      state.batteriesCollected += 1;
      state.comboMult = Math.min(MULT_MAX, state.comboMult + 1);
      state.multTimer = MULT_TIME;
      state.maxMult = Math.max(state.maxMult, state.comboMult);
      state.events.push({ mult: state.comboMult, type: 'battery' });
      state.pops.push({
        life: 1.3,
        text: state.comboMult >= MULT_MAX ? 'SOLAR SURGE!' : `+${b.value.toFixed(0)} kWh  x${state.comboMult}`,
        x: b.x,
        y: by - BATTERY_R - 4,
      });
    }
  });

  if (checkCollision(state)) {
    state.status = 'over';
    state.thrusting = false;
    state.summary = buildSummary(state);
    state.events.push({ type: 'crash' });
  }
};
